import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import {
  startGame,
  fetchGameInfo,
  fetchLobbyByJoinCode,
  joinGame,
  joinTeam,
  setLobbyReady,
  addAiTeam,
  removeAiTeam,
  facilitatorEnterGame,
  facilitatorJoinTeamAsPlayer,
  facilitatorLeaveTeam,
  switchTeam,
  resetGameSession,
  skipTutorialYear,
  facilitatorJoinLobby
} from 'store/auth'
import { selectSkipTutorialEnabled } from 'store/settings'
import ROUTES, { getLobbyUrl } from 'constants/routes'
import { toast } from 'react-toastify'
import { getEmailFromAuth0User } from 'utils/auth0'
import { USER_TYPES } from 'constants/users'
import {
  findFacilitatorEntry,
  getFacilitatorSeatTeamId,
  facilitatorActsAsSeatedPlayer
} from 'utils/facilitatorLobby'

import PlayerProfile from './PlayerProfile'
import GameHeader from './GameHeader'
import TeamsSection from './TeamsSection'
import LobbyActions from './Actions'
import LobbySettingsCard from './LobbySettingsCard'

export default function Lobby() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const { joinCode: joinCodeFromUrl } = useParams()
  const { user, isLoading: auth0Loading } = useAuth0()

  const {
    lastCreatedGame,
    gameInfo,
    lobbyTeams,
    loggedIn,
    userType: authUserType
  } = useSelector((store) => store.auth)
  const apiUserType = useSelector((store) => store.user.info.data?.user_type)
  const skipTutorialEnabled = useSelector(selectSkipTutorialEnabled)
  const email = getEmailFromAuth0User(user)
  const hasToken = !!loggedIn
  const sessionUserType = apiUserType || authUserType
  const isFacilitatorSession = sessionUserType === USER_TYPES.facilitator
  const canObserveStartedGames = isFacilitatorSession && gameInfo?.aasm_state === 'started'

  const [starting, setStarting] = useState(false)
  const [readyLoading, setReadyLoading] = useState(false)
  const [joiningFromUrl, setJoiningFromUrl] = useState(false)
  const [joiningTeamSlot, setJoiningTeamSlot] = useState(null)
  const [addingAiSlot, setAddingAiSlot] = useState(null)
  const [removingAiTeamId, setRemovingAiTeamId] = useState(null)
  const [enteringTeamId, setEnteringTeamId] = useState(null)
  const attemptedJoinForRef = useRef(null)

  const adminGameId = lastCreatedGame?.game_id
  const currentJoinCode =
    lastCreatedGame?.join_code ||
    gameInfo?.join_code ||
    joinCodeFromUrl?.trim().toUpperCase() ||
    null
  const normalizedUrlCode = joinCodeFromUrl?.trim().toUpperCase() || null
  const lobbyJoinCodeMatchesUrl =
    Boolean(normalizedUrlCode) &&
    Boolean(gameInfo?.join_code) &&
    gameInfo.join_code.trim().toUpperCase() === normalizedUrlCode

  const teams = lobbyTeams || gameInfo?.teams || []
  const facilitators = gameInfo?.facilitators || []
  const currentFacilitator =
    isFacilitatorSession && email ? findFacilitatorEntry(facilitators, email) : null
  const facilitatorSeatTeamId = getFacilitatorSeatTeamId(currentFacilitator)
  const facilitatorIsPlayer = facilitatorActsAsSeatedPlayer(currentFacilitator)
  const visibleFacilitators = useMemo(() => {
    const facilitatorsByIdentity = new Map()

    facilitators.forEach((facilitator) => {
      const identityKey = facilitator.email?.trim().toLowerCase() || `id:${facilitator.id}`
      const currentRank =
        Number(Boolean(facilitator.is_player)) +
        Number(Boolean(facilitator.current_team_id || facilitator.player_team_id))
      const existing = facilitatorsByIdentity.get(identityKey)
      const existingRank =
        Number(Boolean(existing?.is_player)) +
        Number(Boolean(existing?.current_team_id || existing?.player_team_id))

      if (!existing || currentRank > existingRank) {
        facilitatorsByIdentity.set(identityKey, facilitator)
      }
    })

    return Array.from(facilitatorsByIdentity.values()).sort((left, right) => {
      if (left.is_player === right.is_player) {
        return (left.alias || left.email || '').localeCompare(right.alias || right.email || '')
      }
      return Number(Boolean(left.is_player)) - Number(Boolean(right.is_player))
    })
  }, [facilitators])
  const humanTeams = teams.filter((team) => !team.ai && !team.decision_strategy_name)
  const hasAiTeamAlready = teams.some((team) => team?.ai || team?.decision_strategy_name)
  const canAddAi = !hasAiTeamAlready
  const currentPlayerFromApi = gameInfo?.current_player
  const currentPlayerStableId = currentPlayerFromApi?.id ?? null
  const currentPlayerStableTeamId = currentPlayerFromApi?.team_id ?? null
  let currentTeam = null
  if (currentPlayerFromApi != null) {
    currentTeam = humanTeams.find((teamItem) => teamItem.id === currentPlayerFromApi.team_id)
  } else if (facilitatorSeatTeamId != null) {
    currentTeam = humanTeams.find((teamItem) => teamItem.id === facilitatorSeatTeamId)
  }
  const currentTeamReady = currentTeam?.lobby_ready ?? false
  const pendingTeamChoice = lastCreatedGame?.pending_team_choice && !currentTeam
  const currentPlayer =
    currentPlayerFromApi ??
    currentTeam?.players?.find((p) => p.email && p.email.toLowerCase() === email?.toLowerCase()) ??
    (pendingTeamChoice && email
      ? { alias: email.split('@')[0] || 'You', email, game_role: null, id: null, team_id: null }
      : null)
  const takenRolesExcludingSelf = (currentTeam?.players || [])
    .filter((p) => p.id !== currentPlayer?.id)
    .map((p) => p.game_role)
    .filter(Boolean)
  const hasJoinedThisGame =
    (lastCreatedGame?.join_code?.toUpperCase() === normalizedUrlCode &&
      !lastCreatedGame?.pending_team_choice) ||
    (gameInfo?.current_player && lobbyJoinCodeMatchesUrl) ||
    (isFacilitatorSession && lobbyJoinCodeMatchesUrl)

  // Status message: "Waiting for X more players to be ready..."
  const statusMessage = useMemo(() => {
    const notReadyHumanTeams = humanTeams.filter((team) => !team.lobby_ready)
    const count = notReadyHumanTeams.length
    if (count > 0) {
      return t('lobby.waiting_for_x_players', { count })
    }
    return t('lobby.waiting_for_players')
  }, [humanTeams, t])

  // Clear stale game state when lobby URL join code does not match stored game
  useEffect(() => {
    if (
      normalizedUrlCode &&
      lastCreatedGame?.join_code &&
      lastCreatedGame.join_code.toUpperCase() !== normalizedUrlCode
    ) {
      dispatch(resetGameSession())
    }
  }, [dispatch, normalizedUrlCode, lastCreatedGame?.join_code])

  // Facilitators: public lobby for URL join code first (authoritative game scope vs stale /user/game)
  useEffect(() => {
    if (!isFacilitatorSession || !normalizedUrlCode) return
    dispatch(fetchLobbyByJoinCode(normalizedUrlCode))
  }, [dispatch, isFacilitatorSession, normalizedUrlCode])

  // Facilitators who are players in this lobby need authenticated game payload (after lobby matches URL)
  useEffect(() => {
    if (!isFacilitatorSession || !hasToken || !normalizedUrlCode) return
    if (!lobbyJoinCodeMatchesUrl) return
    if (!facilitatorIsPlayer && !gameInfo?.current_player) return

    dispatch(fetchGameInfo(normalizedUrlCode))
  }, [
    dispatch,
    isFacilitatorSession,
    hasToken,
    normalizedUrlCode,
    lobbyJoinCodeMatchesUrl,
    facilitatorIsPlayer,
    currentPlayerStableId,
    currentPlayerStableTeamId
  ])

  // Started game + facilitator already a player in this game → board (join code always wins over stale session)
  useEffect(() => {
    if (!isFacilitatorSession || !canObserveStartedGames) return
    if (!facilitatorIsPlayer) return
    if (!lobbyJoinCodeMatchesUrl) return

    history.replace(ROUTES.APP_URL_HOME)
  }, [
    isFacilitatorSession,
    canObserveStartedGames,
    facilitatorIsPlayer,
    lobbyJoinCodeMatchesUrl,
    history
  ])

  // Fetch game/lobby data
  useEffect(() => {
    if (isFacilitatorSession) {
      return
    }

    const shouldFetchPublicLobby =
      normalizedUrlCode && (!hasToken || !hasJoinedThisGame || lastCreatedGame?.pending_team_choice)

    if (shouldFetchPublicLobby) {
      dispatch(fetchLobbyByJoinCode(normalizedUrlCode))
    }
    const joinCodeToFetch = currentJoinCode || normalizedUrlCode
    const shouldFetchGameInfo =
      hasToken &&
      joinCodeToFetch &&
      (!normalizedUrlCode ||
        (lastCreatedGame?.join_code?.toUpperCase() === normalizedUrlCode &&
          !lastCreatedGame?.pending_team_choice))
    if (shouldFetchGameInfo) {
      const expectedCode = normalizedUrlCode || joinCodeToFetch
      dispatch(fetchGameInfo(expectedCode || null))
    }
  }, [
    dispatch,
    hasToken,
    hasJoinedThisGame,
    currentJoinCode,
    normalizedUrlCode,
    lastCreatedGame?.join_code,
    lastCreatedGame?.pending_team_choice,
    isFacilitatorSession
  ])

  // Keep URL in sync with game
  useEffect(() => {
    if (currentJoinCode && !normalizedUrlCode) {
      history.replace(getLobbyUrl(currentJoinCode))
    }
  }, [currentJoinCode, normalizedUrlCode, history])

  // Auto-join when visiting /lobby/:joinCode (players only; facilitators use lobby fetch + spectate)
  useEffect(() => {
    if (isFacilitatorSession || !normalizedUrlCode || !email) return
    if (attemptedJoinForRef.current === normalizedUrlCode) return
    if (gameInfo?.current_player && gameInfo?.join_code === normalizedUrlCode) return
    if (lastCreatedGame?.join_code === normalizedUrlCode && lastCreatedGame?.pending_team_choice)
      return
    if (lastCreatedGame?.join_code === normalizedUrlCode && lastCreatedGame?.is_creator) return

    attemptedJoinForRef.current = normalizedUrlCode
    setJoiningFromUrl(true)

    dispatch(joinGame(normalizedUrlCode, email))
      .then((gameData) => {
        if (gameData) {
          if (gameData.aasm_state === 'started') {
            history.replace(ROUTES.APP_URL_HOME)
          }
        } else if (
          !lastCreatedGame?.is_creator ||
          lastCreatedGame?.join_code !== normalizedUrlCode
        ) {
          history.replace(ROUTES.APP_URL_GAME_SELECTION)
        }
      })
      .finally(() => {
        setJoiningFromUrl(false)
      })
  }, [
    isFacilitatorSession,
    normalizedUrlCode,
    email,
    gameInfo?.current_player,
    gameInfo?.join_code,
    lastCreatedGame?.join_code,
    lastCreatedGame?.pending_team_choice,
    lastCreatedGame?.is_creator,
    dispatch,
    history
  ])

  // Facilitator lobby registration: link facilitator to game when opening lobby URL
  useEffect(() => {
    if (!isFacilitatorSession || !normalizedUrlCode || !hasToken) return
    dispatch(facilitatorJoinLobby(normalizedUrlCode))
  }, [dispatch, isFacilitatorSession, normalizedUrlCode, hasToken])

  // Redirect to game selection when no game context
  useEffect(() => {
    const timer = setTimeout(() => {
      const noGameContext = !lastCreatedGame && !gameInfo
      if (noGameContext && !normalizedUrlCode) {
        history.replace(ROUTES.APP_URL_GAME_SELECTION)
      }
    }, 100)
    return () => clearTimeout(timer)
  }, [lastCreatedGame, gameInfo, normalizedUrlCode, history])

  // Redirect if session expired
  useEffect(() => {
    if (auth0Loading || canObserveStartedGames) return
    if (!email) {
      toast.warn(t('lobby.session_expired'))
      history.replace(ROUTES.APP_URL_GAME_SELECTION)
    }
  }, [auth0Loading, email, canObserveStartedGames, history, t])

  const tryStartIfAllReady = useCallback(
    async (teamsData) => {
      const allTeams = teamsData || []
      const human = allTeams.filter((teamItem) => !teamItem.ai && !teamItem.decision_strategy_name)
      const ready =
        allTeams.length >= 2 && human.length >= 1 && human.every((teamItem) => teamItem.lobby_ready)
      if (!ready || !adminGameId || !email) {
        return false
      }
      setStarting(true)
      const started = await dispatch(startGame(adminGameId, email))
      if (!started) {
        setStarting(false)
        return false
      }
      if (skipTutorialEnabled && hasAiTeamAlready) {
        const apiGameId = gameInfo?.id ?? adminGameId
        const skipped = await dispatch(skipTutorialYear(apiGameId))
        if (!skipped) {
          setStarting(false)
          return false
        }
      }
      history.replace(ROUTES.APP_URL_HOME)
      return true
    },
    [dispatch, adminGameId, email, skipTutorialEnabled, hasAiTeamAlready, gameInfo?.id, history]
  )

  // Redirect to game when started (skip while host flow runs start + optional skip tutorial)
  useEffect(() => {
    if (canObserveStartedGames) return
    if (starting) return
    const joinCodeFromApi = gameInfo?.join_code?.trim()
    if (gameInfo?.aasm_state !== 'started' || !joinCodeFromApi) return
    const codeMatch = normalizedUrlCode
      ? joinCodeFromApi.toUpperCase() === normalizedUrlCode
      : joinCodeFromApi.toUpperCase() === currentJoinCode?.toUpperCase()
    if (codeMatch) {
      history.replace(ROUTES.APP_URL_HOME)
    }
  }, [canObserveStartedGames, gameInfo, history, currentJoinCode, normalizedUrlCode, starting])

  // Poll for lobby state
  useEffect(() => {
    if (canObserveStartedGames && !currentTeam?.id) return () => {}
    if (!hasToken || !normalizedUrlCode || !gameInfo?.join_code) return () => {}
    if (gameInfo.join_code.trim().toUpperCase() !== normalizedUrlCode) return () => {}
    if (gameInfo?.aasm_state === 'started') return () => {}

    if (!hasJoinedThisGame) return () => {}

    const pollFacilitatorLobbyOnly =
      isFacilitatorSession && gameInfo?.aasm_state === 'waiting_for_players' && !facilitatorIsPlayer

    const pollInterval = setInterval(() => {
      if (pollFacilitatorLobbyOnly) {
        dispatch(fetchLobbyByJoinCode(normalizedUrlCode))
      } else {
        dispatch(fetchGameInfo(normalizedUrlCode))
      }
    }, 1000)

    return () => clearInterval(pollInterval)
  }, [
    dispatch,
    hasToken,
    normalizedUrlCode,
    hasJoinedThisGame,
    gameInfo?.join_code,
    gameInfo?.aasm_state,
    canObserveStartedGames,
    currentTeam?.id,
    isFacilitatorSession,
    facilitatorIsPlayer
  ])

  const handleReadyClick = useCallback(async () => {
    setReadyLoading(true)
    try {
      const result = await dispatch(setLobbyReady(!currentTeamReady))
      if (result?.teams) {
        await tryStartIfAllReady(result.teams)
      }
    } catch {
      toast.error(t('lobby.start_error'))
    } finally {
      setReadyLoading(false)
    }
  }, [dispatch, currentTeamReady, tryStartIfAllReady, t])

  const handleChooseTeam = useCallback(
    async (teamId, slot) => {
      setJoiningTeamSlot(slot)
      try {
        if (canObserveStartedGames) {
          // In started games, facilitators should only spectate via the explicit spectate button.
          return
        }

        if (isFacilitatorSession && gameInfo?.aasm_state === 'waiting_for_players') {
          if (!normalizedUrlCode) return
          if (facilitatorIsPlayer && currentTeam?.id) {
            const switchingToAnother =
              (teamId && teamId !== currentTeam.id) || (!teamId && slot != null)
            if (switchingToAnother) {
              await dispatch(facilitatorLeaveTeam(normalizedUrlCode))
            }
          }
          await dispatch(facilitatorJoinTeamAsPlayer(teamId || null, normalizedUrlCode, slot))
          return
        }

        if (currentTeam?.id) {
          if (teamId) {
            await dispatch(switchTeam(teamId))
          } else {
            await dispatch(switchTeam(null, slot))
          }
          await dispatch(fetchGameInfo(normalizedUrlCode || currentJoinCode || null))
          return
        }

        if (!adminGameId || !currentJoinCode || !email) return
        await dispatch(joinTeam(adminGameId, currentJoinCode, email, slot))
      } catch (error) {
        toast.error(error.response?.data?.message || t('lobby.start_error'))
      } finally {
        setJoiningTeamSlot(null)
      }
    },
    [
      dispatch,
      adminGameId,
      canObserveStartedGames,
      currentJoinCode,
      currentTeam?.id,
      email,
      t,
      isFacilitatorSession,
      gameInfo?.aasm_state,
      facilitatorIsPlayer,
      normalizedUrlCode
    ]
  )

  const handleAddAiTeam = useCallback(
    async (slot, decisionStrategyName) => {
      if (!adminGameId || !email || !canAddAi) return
      setAddingAiSlot(slot)
      try {
        await dispatch(addAiTeam(adminGameId, email, slot, decisionStrategyName))
      } finally {
        setAddingAiSlot(null)
      }
    },
    [dispatch, adminGameId, email, canAddAi]
  )

  const handleRemoveAiTeam = useCallback(
    async (teamId) => {
      const adminGameIdForApi = gameInfo?.admin_game_id ?? adminGameId
      const gameApiId = gameInfo?.id
      if ((!adminGameIdForApi || !email) && (!gameApiId || !teamId)) return
      setRemovingAiTeamId(teamId)
      try {
        await dispatch(removeAiTeam(adminGameIdForApi, email, teamId, gameApiId))
      } finally {
        setRemovingAiTeamId(null)
      }
    },
    [dispatch, gameInfo?.admin_game_id, gameInfo?.id, adminGameId, email]
  )

  const handleFacilitatorEnterTeam = useCallback(
    async (teamId) => {
      setEnteringTeamId(teamId)
      try {
        await dispatch(facilitatorEnterGame(teamId))
        history.replace(ROUTES.APP_URL_HOME)
      } catch (error) {
        toast.error(error.response?.data?.message || t('lobby.start_error'))
      } finally {
        setEnteringTeamId(null)
      }
    },
    [dispatch, history, t]
  )

  const handleFacilitatorLeaveTeam = useCallback(async () => {
    try {
      await dispatch(facilitatorLeaveTeam(currentJoinCode))
    } catch (error) {
      toast.error(error.response?.data?.message || t('lobby.start_error'))
    }
  }, [dispatch, currentJoinCode, t])

  const handleLeaveGame = () => {
    dispatch(resetGameSession())
    history.replace(ROUTES.APP_URL_GAME_SELECTION)
  }

  const lobbyUrl = currentJoinCode ? `${window.location.origin}${getLobbyUrl(currentJoinCode)}` : ''

  const copyLobbyUrl = () => {
    if (lobbyUrl) {
      navigator.clipboard.writeText(lobbyUrl)
      toast.success(t('lobby.url_copied'))
    }
  }

  const joinCode = currentJoinCode
  const gameName = gameInfo?.name ?? (joiningFromUrl ? t('lobby.joining') : t('lobby.loading'))

  return (
    <div className="lobby fade-in">
      <div className="lobby__container">
        {(currentTeam || (pendingTeamChoice && email)) && (
          <div className="lobby__sidebar">
            <PlayerProfile
              currentPlayer={currentPlayer}
              currentTeam={currentTeam}
              takenRolesExcludingSelf={takenRolesExcludingSelf}
              isGameCreator={!!lastCreatedGame?.is_creator}
            />
            <LobbySettingsCard hasAiTeam={hasAiTeamAlready} />
          </div>
        )}
        <div className="lobby__content">
          <GameHeader
            gameName={gameName}
            joinCode={joinCode}
            gameInfo={gameInfo}
            lastCreatedGame={lastCreatedGame}
            onCopyUrl={copyLobbyUrl}
            isGameCreator={!!lastCreatedGame?.is_creator}
          />
          <TeamsSection
            teams={teams}
            email={email}
            currentTeamId={currentTeam?.id}
            pendingTeamChoice={pendingTeamChoice}
            joiningTeamSlot={joiningTeamSlot}
            onChooseTeam={handleChooseTeam}
            onAddAiTeam={handleAddAiTeam}
            addingAiSlot={addingAiSlot}
            canAddAi={canAddAi}
            isGameCreator={!!lastCreatedGame?.is_creator}
            canObserveStartedGames={canObserveStartedGames}
            gameWaitingForPlayers={gameInfo?.aasm_state === 'waiting_for_players'}
            onFacilitatorEnterTeam={handleFacilitatorEnterTeam}
            enteringTeamId={enteringTeamId}
            onRemoveAiTeam={handleRemoveAiTeam}
            removingAiTeamId={removingAiTeamId}
            facilitators={visibleFacilitators}
            onFacilitatorLeaveTeam={handleFacilitatorLeaveTeam}
            isFacilitatorSession={isFacilitatorSession}
          />
        </div>
      </div>
      <LobbyActions
        pendingTeamChoice={pendingTeamChoice}
        currentTeamReady={currentTeamReady}
        readyLoading={readyLoading}
        starting={starting}
        humanTeamsCount={humanTeams.length}
        joiningFromUrl={joiningFromUrl}
        joiningTeamSlot={joiningTeamSlot}
        onReadyClick={handleReadyClick}
        onLeaveGame={handleLeaveGame}
        isFacilitator={isFacilitatorSession}
        facilitatorIsPlayer={facilitatorIsPlayer}
        enteringTeamId={enteringTeamId}
        statusMessage={statusMessage}
      />
    </div>
  )
}
